import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useOfficeTutorial } from '~/components/OfficeTutorialPopup';
import { useGetDashboardJobInfo } from '~/domain/DashboardJobs/useCases/useGetDashboardJobInfo';
import {
  getShouldUseNewOnboardingPopup,
  getShouldUsePendingReviews,
  shouldUseDsoStructure,
} from '~/growthbook';
import { useAppSelector } from '~/hooks';
import { useFeatureFlagVariant } from '~/hooks/useFeatureFlagVariant';
import { useUserSelector } from '~/hooks/useUserSelector';
import WarningIcon from '~/images/icons/Warning';
import { routes } from '~/routes';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import { feedbackRequire, fetchJobsWithAdjustmts, hideRegistrationPopup } from '../../actions';
import { Box, ConfirmPopup, Text, WarningBanner } from '../../commonComponents';
import { SelectWorkerTypePopup } from '../../commonComponents/popups/SelectWorkerTypePopup/SelectWorkerTypePopup';
import history from '../../browserHistory';
import { Colors } from '../../themes/colors';
import '../../themes/inbox.scss';
import { RemoteConfigUtils } from '../../utils/RemoteConfigUtils';
import { ChargeTimelineDaysPopup } from '../ChargeTimelineDaysPopup';
import Inbox from '../Inbox';
import { PrimaryOfficeContactPopup } from '../PrimaryOfficeContactPopup';
import { ActiveShift } from './ActiveShift';
import { DomainDecommissioningWarningBanner } from './DomainDecommissioning';
import { useReferralPayout } from './hooks/useReferralPayout';
import { ShiftsSection } from './ShiftsSection';
import ShiftsWidget from './ShiftsWidget';
import { TodayShiftCard } from './TodayShiftCard';
import { TopButtons } from './TopButtons';

const Dashboard = () => {
  const { showRegistrationSuccessPopup } = useAppSelector((state) => state.user);
  const { openTutorial } = useOfficeTutorial();
  const dashboardInfo = useSelector((state) => state.job.dashboardInfo);
  const dispatch = useDispatch();
  const selectedOfficeId = useSelector((state) => state.user.selectedOfficeId);
  const user = useSelector((state) => state.user.user);
  const isPendingReviewsEnabled = useRef(getShouldUsePendingReviews()).current;
  // Check if the feature flag is set to 'test' variant for the new shifts widget
  const useNewShiftsWidget = useFeatureFlagVariant('guided-user-journey-dashboard', 'test');
  const { isLoading, refetch } = useGetDashboardJobInfo();

  useEffect(() => {
    refetch();
  }, [dispatch, selectedOfficeId]);

  useEffect(() => {
    if (showRegistrationSuccessPopup && getShouldUseNewOnboardingPopup()) {
      openTutorial('office-tutorial-dashboard-overview');
      dispatch(hideRegistrationPopup());
    }
  }, [dispatch, openTutorial, showRegistrationSuccessPopup]);

  return (
    <div data-cy="dashboard-container" style={{ width: '100%', maxWidth: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          justifyContent: 'space-between',
          alignContent: 'start',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <TopButtons showLocumTenens={user?.address?.[0]?.state === 'FL'} />
        <div
          style={{
            display: 'flex',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          {useNewShiftsWidget ? (
            <ShiftsSection isLoading={isLoading} />
          ) : (
            <ShiftsWidget
              activeCount={dashboardInfo?.activeRequest.toString()}
              scheduledCount={dashboardInfo?.scheduled.toString()}
              actionRequiredCount={dashboardInfo?.actionRequired.toString()}
              isLoading={isLoading}
            />
          )}
        </div>

        {isPendingReviewsEnabled ? (
          <ActiveShift />
        ) : (
          <>
            {dashboardInfo?.numberOfTodayShifts > 0 && (
              <div>
                <TodayShiftCard />
              </div>
            )}
          </>
        )}

        <Inbox history={history} isRootComponent={false} />
      </div>
    </div>
  );
};

const WelcomeBanner = () => (
  <div
    style={{
      background: `linear-gradient(90deg, #6AB8C3 14%, #4FA5B3 100%)`,
      fontFamily: 'Nunito',
      height: '85px',
      overflow: 'hidden',
      position: 'relative',
      width: '100%',
    }}
  >
    <Text
      color="#167C8C"
      fontSize={144}
      style={{
        bottom: 0,
        left: 0,
        letterSpacing: '-4px',
        opacity: 0.25,
        position: 'absolute',
        top: '-60px',
      }}
    >
      Welcome!
    </Text>
    <Text
      bold
      color={Colors.white}
      fontSize={70}
      style={{
        marginLeft: 24,
        position: 'relative',
      }}
    >
      Hey there!
    </Text>
  </div>
);

const dashboardRequestInterval = 1 * 60 * 60 * 1000; // 1 hour in milliseconds

export default () => {
  const dispatch = useDispatch();
  const user = useUserSelector();
  const allowedRequest = user?.allowedRequest || {};
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { showNotAllowedPopup } = locationState || {};
  // Check if the feature flag is set to 'test' variant for the new shifts widget
  const useNewShiftsWidget = useFeatureFlagVariant('guided-user-journey-dashboard', 'test');

  useReferralPayout();

  const canPostTempShifts = allowedRequest?.tempOrMdbShifts;
  const canPostPermanentHire = allowedRequest?.ppJob;
  const showPostingUnavailableWarning = !canPostTempShifts && !canPostPermanentHire;

  const statesToDisableTempRequests = RemoteConfigUtils.getDisabledTempPostStates();
  const showBlockedStateWarning =
    !showPostingUnavailableWarning &&
    (!allowedRequest?.tempOrMdbShifts ||
      statesToDisableTempRequests?.includes(user?.address?.[0]?.state));

  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();

  const showWorkerClassificationWarning =
    isWorkerClassificationEnabled && !user?.workerClassification?.classificationType;

  const selectedOfficeId = useSelector((state) => state.user.selectedOfficeId);

  useEffect(() => {
    if (!user) return;

    const prevDashboardRequestDate = localStorage.getItem(`prevDashboardRequestDate-${user.id}`);

    if (
      !prevDashboardRequestDate ||
      Date.now() - new Date(prevDashboardRequestDate).getTime() > dashboardRequestInterval
    ) {
      dispatch(feedbackRequire());
      dispatch(fetchJobsWithAdjustmts());

      localStorage.setItem(`prevDashboardRequestDate-${user.id}`, new Date().toISOString());
    }
  }, [dispatch, user?.id]);

  return (
    <div
      className="component_container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    >
      {!useNewShiftsWidget && <WelcomeBanner />}

      <Box marginBottom={20}>
        {showBlockedStateWarning && (
          <WarningBanner
            dataCy="dashboard-blocked-state-warning"
            description="GoTu is not offering temporary services in your state as we work on building the best user experience for offices like you. However, “Permanent Hires” are still available!"
            containerStyle={{ width: '100%', alignItems: 'flex-start', marginBottom: 0 }}
            indicatorStyle={{ display: 'none' }}
            iconStyle={{ marginTop: 10, marginLeft: 20, alignSelf: 'start' }}
            descriptionStyle={{ marginRight: 80 }}
          />
        )}

        {((shouldUseDsoStructure() && selectedOfficeId && showWorkerClassificationWarning) ||
          (!shouldUseDsoStructure() && showWorkerClassificationWarning)) && (
          <WarningBanner
            dataCy="dashboard-worker-classification-warning"
            description="Shift requests are unavailable until a worker type is selected for this office. Click here to set your preference."
            descriptionStyle={{ flex: 1, display: 'block' }}
            containerStyle={{ paddingRight: 16, marginBottom: 0 }}
            indicatorStyle={{ display: 'none' }}
            onClick={() => history.push(routes.workerClassificationType)}
            showRightArrow
            secondaryColor={Colors.error_50}
          />
        )}

        <DomainDecommissioningWarningBanner />

        {showPostingUnavailableWarning && (
          <WarningBanner
            dataCy="dashboard-posting-unavailable-warning"
            description="Temporary and permanent job posting is currently disabled. Contact your Account Manager for support."
            descriptionStyle={{ flex: 1, display: 'block' }}
            containerStyle={{ paddingRight: 16, marginBottom: 0 }}
            indicatorStyle={{ display: 'none' }}
            secondaryColor={Colors.error_50}
          />
        )}
      </Box>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 24,
          paddingTop: 0,
          background: Colors.neutral_100,
          maxWidth: 1400,
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        <Dashboard />
      </div>

      <SelectWorkerTypePopup />
      <PrimaryOfficeContactPopup />

      {showNotAllowedPopup && (
        /* @ts-expect-error -- not typed */
        <ConfirmPopup
          Icon={() => <WarningIcon size={80} />}
          title="Select Office"
          titleStyle={{
            marginTop: 20,
          }}
          description={
            'To ensure your action is directed to the right location, please select an office by tapping "Switch Account" on the "Dashboard" page first.'
          }
          descriptionStyle={{
            marginBottom: 40,
          }}
          rightButtonText={'Close'}
          rightButtonAction={() => {
            navigate('/');
          }}
        />
      )}

      <ChargeTimelineDaysPopup screenName="Dashboard" />
    </div>
  );
};
